import { UserInvoiceCategory } from 'components/UserInvoice/types';
import { omit } from 'lodash';
import Router, { useRouter } from 'next/router';
import { Key, useCallback, useMemo } from 'react';
import { create } from 'zustand';

export type InvoicePathReturn = [string | null, (category?: Key | UserInvoiceCategory | false) => void, string[]];

const usePathHistory = create<{
    history: string[];
    pushHistory: (item: string) => void;
    clearHistory: () => void;
}>()((set) => ({
    history: [],
    pushHistory: (item) => set((state) => ({ history: [...state.history, item] })),
    clearHistory: () => set({ history: [] })
}));

export const useInvoicePath = (): InvoicePathReturn => {
    const { query } = useRouter();
    const { history, pushHistory } = usePathHistory();

    const path = useMemo(() => query?.category as string, [query]);

    // reads the router at call time: before isReady the query is still empty and pushing it
    // would drop [slug] from the dynamic pathname, which Next throws on
    const setPath = useCallback<InvoicePathReturn[1]>((c) => {
        if (!Router.isReady) return;
        const { query: currentQuery, pathname } = Router;

        const category = typeof c === 'object' ? c?.id : c;
        pushHistory(currentQuery.category as string);
        const newQuery = { ...currentQuery, category };
        Router.push({ pathname, query: c === false ? omit(currentQuery, 'category') : newQuery }, undefined, { shallow: true });
    }, [pushHistory]);

    return [path, setPath, history];
};

export default useInvoicePath;
